import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Lock, AlertCircle, Loader2, UserPlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const LoginPage = () => {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    let result;
    if (mode === 'login') {
      result = await login(email, password);
    } else {
      if (password.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
      result = await register(name, email, password);
    }
    setLoading(false);
    if (result.success) {
      navigate('/portal');
    } else {
      setError(result.error);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-12">
          <Shield className="w-8 h-8 text-[#00FF41]" />
          <span className="font-['Unbounded'] font-bold text-2xl tracking-tight text-white">
            Drk<span className="text-[#00FF41]">LynX</span>
          </span>
        </Link>

        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          <div className="flex items-center gap-2 mb-6">
            {mode === 'login' ? <Lock className="w-4 h-4 text-[#00FF41]" /> : <UserPlus className="w-4 h-4 text-[#00FFFF]" />}
            <span className="font-['JetBrains_Mono'] text-xs tracking-[0.2em] uppercase" style={{ color: mode === 'login' ? '#00FF41' : '#00FFFF' }}>
              // {mode === 'login' ? 'Sign In' : 'Create Account'}
            </span>
          </div>

          <h1 data-testid="login-heading" className="font-['Unbounded'] font-bold text-xl tracking-tighter mb-2 text-white">
            {mode === 'login' ? 'Mission Control' : 'Join DrkLynX'}
          </h1>
          <p className="font-['Outfit'] font-light text-sm text-[#737373] mb-8">
            {mode === 'login' ? 'Authenticate to access the dashboard.' : 'Create your quantum-hardened account.'}
          </p>

          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-4 py-3 mb-6">
              <AlertCircle className="w-4 h-4 text-[#FF3B30] shrink-0" />
              <span className="font-['Outfit'] text-sm text-[#FF3B30]">{error}</span>
            </div>
          )}

          <form data-testid="login-form" onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2">Name</label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#737373] font-['Outfit']"
                />
              </div>
            )}
            <div>
              <label className="block font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#737373] font-['Outfit']"
              />
            </div>
            <div>
              <label className="block font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'register' ? 'Min 6 characters' : 'Enter password'}
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#737373] font-['Outfit']"
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 border py-3 text-xs font-['JetBrains_Mono'] tracking-[0.15em] uppercase transition-colors duration-300 disabled:opacity-50 mt-6"
              style={{
                borderColor: mode === 'login' ? '#00FF41' : '#00FFFF',
                color: mode === 'login' ? '#00FF41' : '#00FFFF',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = mode === 'login' ? '#00FF41' : '#00FFFF'; e.target.style.color = '#000'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = mode === 'login' ? '#00FF41' : '#00FFFF'; }}
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {mode === 'login' ? 'Authenticating...' : 'Creating account...'}</> : mode === 'login' ? 'Access Dashboard' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <button
              data-testid="auth-mode-toggle"
              onClick={switchMode}
              className="font-['Outfit'] text-sm text-[#A3A3A3] hover:text-white transition-colors duration-300"
            >
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <span className="font-medium" style={{ color: mode === 'login' ? '#00FFFF' : '#00FF41' }}>
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </span>
            </button>
            {mode === 'login' && (
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="block font-['Outfit'] text-sm text-[#737373] hover:text-[#FFD700] transition-colors duration-300"
              >
                Forgot your password?
              </Link>
            )}
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="font-['JetBrains_Mono'] text-[10px] tracking-[0.15em] uppercase text-[#737373] hover:text-[#00FF41] transition-colors duration-300">
            Back to DrkLynX.com
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
